import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import toastr from "toastr";
import {
  trackPromotionSelected,
  trackPromotionViewed,
} from "../promotionAnalytics";
import {
  clearBoostValue,
  clearRiskFreeValue,
  DISCOUNTED_STAT_PROMO_USED_EVENT,
  getBoostValue,
  getRiskFreeValue,
  hasRecentDiscountedStatPromoUse,
  PROMO_STATE_CHANGE_EVENT,
  setBoostValue,
  setRiskFreeValue,
  TAP_IN_PROMO_UNAVAILABLE_EVENT,
} from "../promoState";
import { getLiveHeroFamily, getSportFamily } from "../sportFamilies.js";
const { getEventAvailabilityKey } = require("./gameAvailability.js");

const MOBILE_PROMO_QUERY = "(max-width: 767.98px)";
const HERO_ACTION_BY_TYPE = {
  "deposit-match": "deposit-match",
  "stat-discount": "free-square",
  "throwback": "risk-free",
  "payout-boost": "boost",
  "refer-a-friend": "refer-friend"
};
const WIN_100_BOARD_URL = "/board/?ww_win_100=1&ww_tier_override=3&shrk=1";
const LIVE_HERO_LABEL_BY_TYPE = {
  "live-football": "Live Football",
  "live-basketball": "Live Basketball",
  "live-baseball": "Live Baseball"
};
const LIVE_HERO_PREVIEW_EVENTS = {
  football: {
    id: "hero-preview-football",
    _topPlaysLeague: "nfl",
    sport_family: "football",
    away: "Buffalo Bills",
    home: "Kansas City Chiefs",
    away_score: 17,
    home_score: 21,
    status_detail: "Q3 · 04:21"
  },
  basketball: {
    id: "hero-preview-basketball",
    _topPlaysLeague: "nba",
    sport_family: "basketball",
    away: "Boston Celtics",
    home: "Los Angeles Lakers",
    away_score: 82,
    home_score: 79,
    status_detail: "Q3 · 02:14"
  },
  baseball: {
    id: "hero-preview-baseball",
    _topPlaysLeague: "mlb",
    sport_family: "baseball",
    away: "San Francisco Giants",
    home: "Los Angeles Dodgers",
    away_score: 3,
    home_score: 4,
    status_detail: "Bottom 7th"
  }
};
const PROMO_DISMISS_STORAGE_KEY = "ww-sports-promos-dismissed";
const FREE_SQUARE_TUESDAY_DAY = 2;
const FREE_SQUARE_DEFAULT_DESCRIPTION = "Limited-time discounted plays with better odds.";
const FREE_SQUARE_TUESDAY_PENDING_DESCRIPTION = "Discount lines on Big Game's.";
const ACTIVE_PROMO_SORT_OFFSET = -10;
const DAILY_PROMO_SORT_OFFSET = 10;
const EVERGREEN_PROMO_SORT_OFFSET = 20;
const PROMO_INFO_DETAILS = {
  "deposit-match": {
    title: "Deposit Match",
    intro: "Eligible customers can receive bonus funds with a qualifying deposit.",
    steps: [
      "Open the offer to see the match available for your account.",
      "Make a qualifying deposit while the offer is available.",
      "The eligible bonus is added as promo funds."
    ],
    rules: [
      "Match amount, deposit minimums, and limits depend on the offer shown to your account.",
      "Promo funds are separate from paid funds and are subject to the promotional terms.",
      "This offer cannot be combined with another promotion on the same entry."
    ],
    note: "Final eligibility and match details are shown when you claim the offer."
  },
  "free-square": {
    title: "🎁 Tap-In Tuesday",
    intro: "Tap-In offers are discounted stat lines that make an eligible pick easier to hit.",
    steps: [
      "Open the offer to see which discounted lines are currently live.",
      "Add an eligible discounted pick to your lineup.",
      "Submit the lineup while that line is still available."
    ],
    rules: [
      "Players, stats, and discounted values can change or be removed before entry submission.",
      "Availability is based on account eligibility.",
      "A discounted-line promotion cannot be combined with another promotion on the same entry."
    ]
  },
  "money-back": {
    title: "💪 Throwback Thur.",
    intro: "Throwback Thursday protects your next eligible Thursday entry. If that entry loses, its entry amount is returned as promo credit, up to $5.",
    steps: [
      "Activate Throwback Thursday before submitting your entry.",
      "Build your lineup and pay the full entry amount from your paid balance.",
      "If the entry loses, up to $5 is added to your promo balance after the entry settles."
    ],
    rules: [
      "Available on Thursday only and limited to one protected entry in a rolling 7-day period.",
      "You need at least $5 in paid balance, and your paid balance must cover the full entry amount.",
      "Protection applies to the next eligible entry submitted after activation.",
      "It cannot be combined with a payout boost, discounted-line offer, or another promotion.",
      "If the entry wins, the normal payout applies and no protection credit is issued."
    ]
  },
  "boost-sunday": {
    title: "Sunday Boost",
    intro: "Sunday Boost increases the payout value on your next eligible Sunday entry by 20%.",
    steps: [
      "Activate the boost before submitting your entry.",
      "Build your lineup and pay the full entry amount from your paid balance.",
      "The boosted payout is shown in your entry slip before you submit."
    ],
    rules: [
      "Available on Sunday only and subject to account eligibility.",
      "Your paid balance must cover the full entry amount.",
      "The boost applies to the next eligible entry after activation.",
      "It cannot be combined with another promotion on the same entry."
    ]
  },
  "refer-friend": {
    title: "Refer a Friend",
    intro: "Invite an eligible new player with your personal referral link and earn a referral reward when they qualify.",
    steps: [
      "Open the referral page and copy your personal link.",
      "Your friend signs up through that link and completes the qualifying requirements.",
      "The referral reward is added after the qualification is confirmed."
    ],
    rules: [
      "The friend must be a new, eligible customer and use your link when signing up.",
      "Reward amounts and qualifying requirements are shown on the referral page.",
      "Duplicate, self-referred, or otherwise ineligible accounts do not qualify."
    ],
    note: "Referral eligibility is confirmed after the qualifying requirements are completed."
  }
};
const INACTIVE_PROMO_MODAL_DETAILS = {
  "risk-free": {
    title: "Throwback Thursday",
    body: "Throwback Thursday is not live right now. Check back Thursday to claim a loss-protected entry before building your lineup. Availability depends on account eligibility and cannot be stacked with other promos."
  },
  boost: {
    title: "Sunday Payout Boost",
    body: "Sunday Payout Boost is not live right now. Check back Sunday to activate the 20% payout boost before building your lineup. Availability depends on account eligibility and cannot be stacked with other promos."
  }
};
const UNAVAILABLE_PROMO_MODAL_DETAILS = {
  "risk-free": {
    title: "Throwback Thursday",
    body: "Throwback Thursday requires at least $5 in paid balance and cannot be stacked with other promos."
  },
  boost: {
    title: "Sunday Payout Boost",
    body: "Sunday Payout Boost is live today, but this account is not eligible to claim it right now. Availability depends on account eligibility, recent promo usage, and cannot be stacked with other promos."
  }
};
const WEEKDAY_INDEX_BY_NAME = {
  Sunday: 0,
  Monday: 1,
  Tuesday: 2,
  Wednesday: 3,
  Thursday: 4,
  Friday: 5,
  Saturday: 6
};

const getLosAngelesDay = () => {
  try {
    const weekday = new Intl.DateTimeFormat("en-US", {
      weekday: "long",
      timeZone: "America/Los_Angeles"
    }).format(new Date());

    return WEEKDAY_INDEX_BY_NAME[weekday] ?? new Date().getDay();
  } catch (e) {
    return new Date().getDay();
  }
};

const getOrdinal = (value) => {
  const number = Number.parseInt(value, 10);
  if (!Number.isFinite(number)) return "";
  const mod100 = number % 100;
  const suffix = mod100 >= 11 && mod100 <= 13
    ? "th"
    : ({ 1: "st", 2: "nd", 3: "rd" }[number % 10] || "th");
  return `${number}${suffix}`;
};

const getLiveEventStatusLabel = (game) => {
  const detail = String(
    game?.status_detail || game?.status_description || game?.period_label || ""
  ).trim();
  const clock = String(game?.clock || game?.display_clock || "").trim();
  const period = String(game?.period || game?.current_period || "").trim();

  if (detail && detail.toLowerCase() !== "live") {
    return clock && !detail.includes(clock) ? `${detail} · ${clock}` : detail;
  }

  if (period) {
    const league = String(game?._topPlaysLeague || "").toLowerCase();
    const family = game?.sport_family || getSportFamily(league);
    const ordinal = getOrdinal(period);
    const periodText = ["basketball", "football"].includes(family)
      ? `Q${period}`
      : family === "baseball"
        ? `${ordinal || period} inning`
        : ["nhl", "ohockey"].includes(league)
          ? `${ordinal || period} period`
          : `Period ${period}`;
    return clock ? `${periodText} · ${clock}` : periodText;
  }

  return clock || "Live now";
};

const getDaysUntilWeekday = (targetDay, currentDay) => (
  ((targetDay - currentDay) + 7) % 7
);

const isMobilePromoViewport = () => {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return false;
  }

  return window.matchMedia(MOBILE_PROMO_QUERY).matches;
};

const getTodayDismissKey = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getDismissedOfferKeys = () => {
  try {
    const rawValue = window.localStorage.getItem(PROMO_DISMISS_STORAGE_KEY);
    if (!rawValue) {
      return [];
    }

    const parsedValue = JSON.parse(rawValue);
    if (
      !parsedValue ||
      parsedValue.date !== getTodayDismissKey() ||
      !Array.isArray(parsedValue.keys)
    ) {
      return [];
    }

    return parsedValue.keys;
  } catch (e) {
    return [];
  }
};

const getHiddenFieldValue = (id) => {
  if (typeof document === "undefined") {
    return "";
  }

  return document.getElementById(id)?.value ?? "";
};

const getPromoDisplayConfig = () => {
  if (typeof document === "undefined") {
    return { enabled: false, slides: [], adLandingVisit: false };
  }

  const configElement = document.getElementById("ww-promo-display-config");
  if (!configElement?.textContent) {
    return { enabled: false, slides: [], adLandingVisit: false };
  }

  try {
    const config = JSON.parse(configElement.textContent);
    return {
      enabled: Boolean(config?.enabled),
      slides: Array.isArray(config?.slides) ? config.slides : [],
      adLandingVisit: Boolean(config?.adLandingVisit),
      previewType: typeof config?.previewType === "string" ? config.previewType : "",
      liveEvents: Array.isArray(config?.liveEvents)
        ? config.liveEvents
        : (config?.liveEvent && typeof config.liveEvent === "object" ? [config.liveEvent] : [])
    };
  } catch (e) {
    return { enabled: false, slides: [], adLandingVisit: false };
  }
};

const getHiddenMoneyValue = (id) => {
  const parsedValue = Number.parseFloat(getHiddenFieldValue(id));
  return Number.isFinite(parsedValue) ? parsedValue : 0;
};

const isFreeSquareModalAvailable = () => {
  if (typeof document === "undefined") {
    return false;
  }

  return Boolean(document.getElementById("free-squares-modal"));
};

const getActivePromoAthleteFromModal = () => {
  const offer = document.querySelector("#free-squares-modal .ww-tap-in-offer");
  const pickButton = offer?.querySelector("[data-ww-tap-pick]");
  if (!offer || !pickButton) {
    return null;
  }

  const statLine = offer.querySelector(".ww-tap-in-offer__main > span")?.cloneNode(true);
  statLine?.querySelectorAll("b, em").forEach((element) => element.remove());

  return {
    name: pickButton.dataset.wwTapPick || offer.querySelector("strong")?.textContent?.trim() || "",
    image: offer.querySelector("img")?.src || "",
    stat_display: statLine?.textContent?.replace(/\s+/g, " ").trim() || "",
    stat: pickButton.dataset.wwTapStatValue || "",
    og_value: pickButton.dataset.wwTapOgValue || ""
  };
};

const getRiskFreeQueryValue = () => {
  if (typeof window === "undefined") {
    return "";
  }

  const searchParams = new URLSearchParams(window.location.search);
  return searchParams.get("risk-free") || searchParams.get("risk_free") || "";
};

const removeRiskFreeQueryValue = () => {
  if (typeof window === "undefined" || !window.history?.replaceState) {
    return;
  }

  const url = new URL(window.location.href);
  url.searchParams.delete("risk-free");
  url.searchParams.delete("risk_free");
  window.history.replaceState({}, document.title, `${url.pathname}${url.search}${url.hash}`);
};

const getPromoMatchEligibility = () => {
  if (typeof document === "undefined") {
    return false;
  }

  return document.getElementById("amount-react")?.dataset?.promoMatchEligible === "1";
};

const getDefaultMatchCode = () => (
  document.getElementById("amount-react")?.dataset?.defaultMatchCode || ""
);

const getBoostEligibility = () => getHiddenFieldValue("boost-eligible") === "1";

const getTapInEligibility = () => getHiddenFieldValue("tap-in-promo-eligible") === "1";

const hasPaidBalance = () => getHiddenMoneyValue("balance") > 0;

const hasRiskFreePaidBalance = () => getHiddenMoneyValue("balance") >= 5;

const getRiskFreeEligibility = () => (
  getHiddenFieldValue("risk-free-eligible") !== "0" && hasRiskFreePaidBalance()
);

const getResolvedActivePromoText = (activePromoTextOverride = "") => (
  String(activePromoTextOverride || "").trim() || getHiddenFieldValue("promo-athletes-active").trim()
);

const getFreeSquareDescription = (activePromoTextOverride = "") => {
  const activePromoText = getResolvedActivePromoText(activePromoTextOverride);

  if (!activePromoText) {
    if (isFreeSquareModalAvailable()) {
      return "Active - discounted lines are live now.";
    }

    return getLosAngelesDay() === FREE_SQUARE_TUESDAY_DAY
      ? FREE_SQUARE_TUESDAY_PENDING_DESCRIPTION
      : FREE_SQUARE_DEFAULT_DESCRIPTION;
  }

  return `Active - ${activePromoText}`;
};

const getFreeSquareActivePromoCount = (activePromoTextOverride = "", activePromoCountOverride = 0) => {
  const normalizedActivePromoCount = Number(activePromoCountOverride) || 0;
  if (normalizedActivePromoCount > 0) {
    return normalizedActivePromoCount;
  }

  const activePromoText = getResolvedActivePromoText(activePromoTextOverride);

  if (!activePromoText) {
    return isFreeSquareModalAvailable() ? 1 : 0;
  }

  const parsedCount = activePromoText.split("•").filter((promoText) => promoText.trim()).length;

  if (parsedCount > 0) {
    return parsedCount;
  }

  return isFreeSquareModalAvailable() ? 1 : 0;
};

const getFreeSquareTitle = (activePromoTextOverride = "", activePromoCountOverride = 0) => {
  const activePromoCount = getFreeSquareActivePromoCount(activePromoTextOverride, activePromoCountOverride);

  if (activePromoCount > 0 && getLosAngelesDay() !== FREE_SQUARE_TUESDAY_DAY) {
    return activePromoCount > 1 ? "Active Discounts" : "Active Discount";
  }

  return "🎁 Tap-In Tuesday";
};

const PROMO_CONFETTI_SRC = "https://cdn.jsdelivr.net/npm/canvas-confetti@1.9.3/dist/confetti.browser.min.js";

const loadPromoConfetti = () => {
  if (typeof window === "undefined") {
    return Promise.resolve(null);
  }

  if (typeof window.wwPromoCelebrate === "function") {
    return Promise.resolve(window.wwPromoCelebrate);
  }

  if (typeof window.confetti === "function") {
    return Promise.resolve(() => {
      window.confetti({
        particleCount: 120,
        spread: 70,
        origin: { y: 0.7 }
      });
    });
  }

  if (window.__wwPromoConfettiPromise) {
    return window.__wwPromoConfettiPromise;
  }

  window.__wwPromoConfettiPromise = new Promise((resolve) => {
    const existingScript = document.querySelector(`script[src="${PROMO_CONFETTI_SRC}"]`);

    const resolveConfetti = () => {
      if (typeof window.wwPromoCelebrate === "function") {
        resolve(window.wwPromoCelebrate);
        return;
      }

      if (typeof window.confetti === "function") {
        resolve(() => {
          window.confetti({
            particleCount: 120,
            spread: 70,
            origin: { y: 0.7 }
          });
        });
        return;
      }

      resolve(null);
    };

    if (existingScript) {
      if (existingScript.dataset.loaded === "1") {
        resolveConfetti();
        return;
      }

      existingScript.addEventListener("load", resolveConfetti, { once: true });
      existingScript.addEventListener("error", () => resolve(null), { once: true });
      return;
    }

    const script = document.createElement("script");
    script.src = PROMO_CONFETTI_SRC;
    script.async = true;
    script.addEventListener("load", () => {
      script.dataset.loaded = "1";
      resolveConfetti();
    }, { once: true });
    script.addEventListener("error", () => resolve(null), { once: true });
    document.head.appendChild(script);
  });

  return window.__wwPromoConfettiPromise;
};

const celebratePromoActivation = () => {
  loadPromoConfetti().then((celebrate) => {
    if (typeof celebrate === "function") {
      celebrate();
    }
  });
};

const PromoOffers = ({ activePromoText = "", activePromoCount = 0, activePromoAthlete = null, liveEvents = null, previewEvents = [], pendingEventKey = "", exiting = false, entering = false, onSelectLiveEvent, onOpenInfoModal } = {}) => {
  const loggedIn = document.body.classList.contains("logged-in");
  const tierRestricted = getHiddenFieldValue("shrk") === "1";
  const today = getLosAngelesDay();
  const [isMobileViewport, setIsMobileViewport] = useState(isMobilePromoViewport);
  const [dismissedOfferKeys, setDismissedOfferKeys] = useState(getDismissedOfferKeys);
  const [promoMatchEligible, setPromoMatchEligible] = useState(getPromoMatchEligibility);
  const [defaultMatchCode, setDefaultMatchCode] = useState(getDefaultMatchCode);
  const [tapInEligible, setTapInEligible] = useState(getTapInEligibility);
  const [boostEligible, setBoostEligible] = useState(getBoostEligibility);
  const [riskFreeEligible, setRiskFreeEligible] = useState(getRiskFreeEligibility);
  const [freeSquareDescription, setFreeSquareDescription] = useState(() => getFreeSquareDescription(activePromoText));
  const [freeSquareTitle, setFreeSquareTitle] = useState(() => getFreeSquareTitle(activePromoText, activePromoCount));
  const [freeSquareActivePromoCount, setFreeSquareActivePromoCount] = useState(() => getFreeSquareActivePromoCount(activePromoText, activePromoCount));
  const [discountedStatPromoUsed, setDiscountedStatPromoUsed] = useState(hasRecentDiscountedStatPromoUse);
  const [tapInUnavailable, setTapInUnavailable] = useState(false);
  const [boostActive, setBoostActive] = useState(getBoostValue);
  const [riskFreeActive, setRiskFreeActive] = useState(() => Boolean(getRiskFreeValue()));
  const [inactivePromoModal, setInactivePromoModal] = useState(null);
  const [promoInfoModal, setPromoInfoModal] = useState(null);
  const [win100ModalOpen, setWin100ModalOpen] = useState(false);
  const [win100DetailsVisible, setWin100DetailsVisible] = useState(false);
  const [promoDisplayConfig] = useState(getPromoDisplayConfig);
  const [activeHeroSlide, setActiveHeroSlide] = useState(0);
  const heroSliderRef = useRef(null);
  const heroSectionRef = useRef(null);
  const [heroEnteringReady, setHeroEnteringReady] = useState(false);
  const [heroEnteringActive, setHeroEnteringActive] = useState(entering);
  const viewedPromotionKeysRef = useRef(new Set());
  const resolvedActivePromoAthlete = getActivePromoAthleteFromModal() || activePromoAthlete;
  const resolvedLiveEvents = Array.isArray(liveEvents)
    ? liveEvents
    : (promoDisplayConfig.liveEvents || []);
  const resolvedPreviewEvents = Array.isArray(previewEvents) ? previewEvents : [];

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return undefined;
    }

    const mediaQuery = window.matchMedia(MOBILE_PROMO_QUERY);
    const handleViewportChange = (event) => {
      setIsMobileViewport(event.matches);
    };

    setIsMobileViewport(mediaQuery.matches);

    if (typeof mediaQuery.addEventListener === "function") {
      mediaQuery.addEventListener("change", handleViewportChange);
      return () => mediaQuery.removeEventListener("change", handleViewportChange);
    }

    mediaQuery.addListener(handleViewportChange);
    return () => mediaQuery.removeListener(handleViewportChange);
  }, []);

  useEffect(() => {
    if (typeof document === "undefined" || typeof MutationObserver === "undefined") {
      return undefined;
    }

    const syncOfferEligibility = () => {
      setPromoMatchEligible(getPromoMatchEligibility());
      setDefaultMatchCode(getDefaultMatchCode());
      setTapInEligible(getTapInEligibility());
      setBoostEligible(getBoostEligibility());
      setRiskFreeEligible(getRiskFreeEligibility());
      setFreeSquareDescription(getFreeSquareDescription(activePromoText));
      setFreeSquareTitle(getFreeSquareTitle(activePromoText, activePromoCount));
      setFreeSquareActivePromoCount(getFreeSquareActivePromoCount(activePromoText, activePromoCount));
      setDiscountedStatPromoUsed(hasRecentDiscountedStatPromoUse());
    };

    syncOfferEligibility();

    const rootObserver = new MutationObserver(() => {
      syncOfferEligibility();
    });

    rootObserver.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["data-promo-match-eligible", "data-default-match-code", "value"]
    });

    return () => rootObserver.disconnect();
  }, [activePromoText, activePromoCount]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    const syncDiscountedStatPromoUse = () => {
      setDiscountedStatPromoUsed(hasRecentDiscountedStatPromoUse());
    };

    syncDiscountedStatPromoUse();
    window.addEventListener(DISCOUNTED_STAT_PROMO_USED_EVENT, syncDiscountedStatPromoUse);

    return () => window.removeEventListener(DISCOUNTED_STAT_PROMO_USED_EVENT, syncDiscountedStatPromoUse);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    const hideUnavailableTapIn = () => {
      setTapInUnavailable(true);
    };

    window.addEventListener(TAP_IN_PROMO_UNAVAILABLE_EVENT, hideUnavailableTapIn);
    return () => window.removeEventListener(TAP_IN_PROMO_UNAVAILABLE_EVENT, hideUnavailableTapIn);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    const syncPromoState = () => {
      setBoostActive(getBoostValue());
      setRiskFreeActive(Boolean(getRiskFreeValue()));
    };

    syncPromoState();
    window.addEventListener(PROMO_STATE_CHANGE_EVENT, syncPromoState);

    return () => window.removeEventListener(PROMO_STATE_CHANGE_EVENT, syncPromoState);
  }, []);

  useEffect(() => {
    loadPromoConfetti();
  }, []);

  useEffect(() => {
    if (!inactivePromoModal || !window.jQuery) {
      return undefined;
    }

    const modal = window.jQuery("#promo-offer-unavailable-modal");

    const handleHidden = () => {
      setInactivePromoModal(null);
    };

    modal.one("hidden.bs.modal", handleHidden);
    modal.modal("show");

    return () => {
      modal.off("hidden.bs.modal", handleHidden);
    };
  }, [inactivePromoModal]);

  useEffect(() => {
    if (!promoInfoModal || !window.jQuery) {
      return undefined;
    }

    const modal = window.jQuery("#promo-offer-info-modal");

    const handleHidden = () => {
      setPromoInfoModal(null);
    };

    modal.one("hidden.bs.modal", handleHidden);
    modal.modal("show");

    return () => {
      modal.off("hidden.bs.modal", handleHidden);
    };
  }, [promoInfoModal]);

  useEffect(() => {
    if (!win100ModalOpen || !window.jQuery) {
      return undefined;
    }

    const modal = window.jQuery("#win-100-offer-modal");
    const handleHidden = () => {
      setWin100ModalOpen(false);
      setWin100DetailsVisible(false);
    };

    modal.one("hidden.bs.modal", handleHidden);
    modal.modal("show");

    return () => modal.off("hidden.bs.modal", handleHidden);
  }, [win100ModalOpen]);

  useEffect(() => {
    const riskFreeQueryValue = getRiskFreeQueryValue();
    if (!riskFreeQueryValue || !loggedIn || !riskFreeEligible) {
      return;
    }

    setRiskFreeValue(riskFreeQueryValue);

    if (!getRiskFreeValue()) {
      return;
    }

    setRiskFreeActive(true);
    celebratePromoActivation();
    toastr["success"]("Throwback is active on your next eligible entry for up to $5.", "Offer Activated");
    removeRiskFreeQueryValue();
  }, [loggedIn, riskFreeEligible]);

  const offers = [
    {
      key: "deposit-match",
      promotionName: "Deposit Match",
      title: defaultMatchCode === "reload25"
        ? "25% Match Up to $100"
        : (loggedIn ? "Match Your Next Deposit" : "Match Your First Deposit"),
      link: "/promos",
      eyebrow: defaultMatchCode === "reload25"
        ? "⚡ LOW BALANCE OFFER"
        : (loggedIn ? "⚡ Deposit match available" : "⚡ NEW PLAYER • $100 BONUS"),
      backgroundImage: "https://assets.wagedwar.com/wp-content/uploads/2026/07/03101229/discount_bg-1.webp",
      backgroundImageWidth: 660,
      backgroundImageHeight: 231,
      cardStyle: {
        "--promo-card-bg-top": "#111417",
        "--promo-card-bg-bottom": "#292f33",
        "--promo-card-accent": "rgba(42, 255, 79, 0.16)",
        "--promo-card-image-position": "center right"
      },
      action: "deposit-match"
    },
    {
      key: "free-square",
      promotionName: "Tap-In Tuesday",
      title: freeSquareTitle,
      description: freeSquareDescription,
      link: "#",
      eyebrow: "Safer picks, safer wins.",
      activeDay: FREE_SQUARE_TUESDAY_DAY,
      backgroundImage: "https://assets.wagedwar.com/wp-content/uploads/2026/07/03101229/discount_bg-1.webp",
      backgroundImageWidth: 660,
      backgroundImageHeight: 231,
      cardStyle: {
        "--promo-card-bg-top": "#121816",
        "--promo-card-bg-bottom": "#253129",
        "--promo-card-accent": "rgba(42, 255, 79, 0.14)",
        "--promo-card-image-position": "center right",
        "--promo-card-image-size": "cover"
      },
      action: "free-square"
    },
    {
      key: "money-back",
      promotionName: "Throwback Thursday",
      title: isMobileViewport ? "💪 Throwback Thursday" : "💪 Throwback Thur.",
      description: "Get protection on a loss.",
      link: "/promos/",
      eyebrow: "Flex with Win or 💰 Back.",
      activeDay: 4,
      activeCopy: "Flex Active, Win or 💰 Back",
      backgroundImage: "https://assets.wagedwar.com/wp-content/uploads/2026/08/13074620/money_back_promo.webp",
      backgroundImageWidth: 324,
      backgroundImageHeight: 120,
      cardStyle: {
        "--promo-card-bg-top": "#16171a",
        "--promo-card-bg-bottom": "#32363b",
        "--promo-card-accent": "rgba(223, 231, 236, 0.12)"
      },
      action: "risk-free"
    },
    {
      key: "boost-sunday",
      promotionName: "Sunday Boost",
      title: "🚀 Sunday Boost",
      description: "Opt in for boosted payout value.",
      link: "/promos/",
      eyebrow: "Boosted Payouts.",
      activeDay: 0,
      activeCopy: "Activate today",
      backgroundImage: "https://assets.wagedwar.com/wp-content/uploads/2026/04/03120040/boost_rocket.webp",
      backgroundImageWidth: 324,
      backgroundImageHeight: 120,
      cardStyle: {
        "--promo-card-bg-top": "#0f1514",
        "--promo-card-bg-bottom": "#233029",
        "--promo-card-accent": "rgba(42, 255, 79, 0.18)"
      },
      action: "boost"
    },
    {
      key: "refer-friend",
      promotionName: "Refer a Friend",
      title: "🤝 Refer a Friend",
      description: "Invite friends and earn rewards.",
      link: "/invite-friends/",
      eyebrow: "Earn Funds Daily.",
      activeCopy: "$25 per referral",
      backgroundImage: "https://assets.wagedwar.com/wp-content/uploads/2026/06/10094236/refer-friend_banner.webp",
      backgroundImageWidth: 324,
      backgroundImageHeight: 120,
      cardStyle: {
        "--promo-card-bg-top": "#0f1514",
        "--promo-card-bg-bottom": "#233029",
        "--promo-card-accent": "rgba(42, 255, 79, 0.18)"
      },
      action: "refer-friend"
    }
  ];

  const shouldPrioritizeDepositMatch = !loggedIn || !hasPaidBalance();

  const rankedOffers = offers
    .map((offer, index) => {
      const hasActiveDay = Number.isInteger(offer.activeDay);
      const hasActiveFreeSquarePromos =
        offer.action === "free-square" && freeSquareActivePromoCount > 0;
      const isFeatured = hasActiveFreeSquarePromos || (hasActiveDay && offer.activeDay === today);
      const isActivated =
        (offer.action === "boost" && boostActive > 0) ||
        (offer.action === "risk-free" && riskFreeActive);
      const isEligible =
        offer.action === "deposit-match"
          ? (!loggedIn || promoMatchEligible)
          : offer.action === "boost"
            ? (!loggedIn || boostEligible || isActivated)
            : offer.action === "risk-free"
              ? (!loggedIn || riskFreeEligible || (isActivated && hasRiskFreePaidBalance()))
              : true;
      const isClaimableNow =
        (!hasActiveDay || isFeatured) &&
        isEligible &&
        !isActivated;

      return {
        ...offer,
        isFeatured,
        isActivated,
        isEligible,
        isClaimableNow,
        status: isActivated
          ? (offer.action === "boost" ? "Sunday Boost active" : "Protection active")
          : isFeatured
            ? (isEligible ? (offer.activeCopy || offer.eyebrow) : "Live today")
            : offer.eyebrow,
        sortOrder: hasActiveFreeSquarePromos
          ? (shouldPrioritizeDepositMatch ? 1 : ACTIVE_PROMO_SORT_OFFSET)
          : offer.key === "deposit-match"
            ? 0
            : hasActiveDay
              ? DAILY_PROMO_SORT_OFFSET + getDaysUntilWeekday(offer.activeDay, today)
              : EVERGREEN_PROMO_SORT_OFFSET + index
      };
    })
    .sort((left, right) => left.sortOrder - right.sortOrder);

  const visibleOffers = rankedOffers.filter((offer) => {
    const hasActiveTapInPromo = offer.key === "free-square" && freeSquareActivePromoCount > 0;

    if (
      offer.key !== "money-back" &&
      !hasActiveTapInPromo &&
      dismissedOfferKeys.includes(offer.key)
    ) {
      return false;
    }

    if (offer.key === "deposit-match" && loggedIn && !promoMatchEligible) {
      return false;
    }

    if (
      offer.key === "money-back" &&
      loggedIn &&
      !hasRiskFreePaidBalance()
    ) {
      return false;
    }

    if (
      offer.key === "boost-sunday" &&
      loggedIn &&
      today === 0 &&
      !offer.isEligible &&
      !offer.isActivated
    ) {
      return false;
    }

    if (
      offer.key === "free-square" &&
      loggedIn &&
      (!tapInEligible || discountedStatPromoUsed)
    ) {
      return false;
    }

    if (offer.key === "free-square" && tapInUnavailable) {
      return false;
    }

    return true;
  });

  const heroSlides = tierRestricted || !promoDisplayConfig.enabled
    ? []
    : promoDisplayConfig.slides.reduce((slides, slide, index) => {
        const liveFamily = getLiveHeroFamily(slide.type);
        if (liveFamily) {
          const isPreview = promoDisplayConfig.previewType === slide.type;
          const liveEvent = resolvedLiveEvents.find((game) => (
            (game?.sport_family || getSportFamily(game?._topPlaysLeague)) === liveFamily
          ));
          const previewEvent = isPreview && resolvedPreviewEvents.find((game) => (
            (game?.sport_family || getSportFamily(game?._topPlaysLeague)) === liveFamily
          ));
          const matchingLiveEvent = liveEvent || (previewEvent
            ? {
                ...previewEvent,
                status_detail: previewEvent.status_detail || "Preview matchup"
              }
            : (isPreview ? LIVE_HERO_PREVIEW_EVENTS[liveFamily] : null));
          if (matchingLiveEvent) {
            const fallbackTitle = LIVE_HERO_LABEL_BY_TYPE[slide.type] || "Live Event";
            slides.push({
              ...slide,
              index,
              offer: {
                key: `live-event-${matchingLiveEvent._topPlaysLeague || "sport"}-${matchingLiveEvent.id}`,
                promotionName: slide.header || fallbackTitle,
                title: slide.header || fallbackTitle,
                action: "live-event",
                link: "#",
                isClaimableNow: true,
                isActivated: false,
                isEligible: true,
                liveEvent: matchingLiveEvent,
                sportFamily: liveFamily,
                isPreview
              }
            });
          }
          return slides;
        }

        if (slide.type === "ad-landing") {
          if (!loggedIn && promoDisplayConfig.adLandingVisit) {
            slides.push({
              ...slide,
              index,
              offer: {
                key: `ad-landing-${index}`,
                promotionName: slide.header,
                title: slide.header,
                action: "ad-landing",
                link: "#register-modal",
                isClaimableNow: true,
                isActivated: false,
                isEligible: true
              }
            });
          }
          return slides;
        }

        if (slide.type === "win-100") {
          slides.push({
            ...slide,
            index,
            offer: {
              key: "win-100",
              promotionName: slide.header || "Win $100",
              title: slide.header || "Win $100",
              action: "win-100",
              link: WIN_100_BOARD_URL,
              isClaimableNow: true,
              isActivated: false,
              isEligible: true
            }
          });
          return slides;
        }

        const action = HERO_ACTION_BY_TYPE[slide.type];
        const offer = visibleOffers.find((candidate) => candidate.action === action);
        if (offer) {
          slides.push({ ...slide, index, offer });
        }
        return slides;
      }, []).sort((left, right) => {
        const priority = (slide) => {
          if (slide.offer.isPreview) return -50;
          if (slide.offer.action === "ad-landing") return -40;
          if (slide.offer.action === "win-100") return -40;
          if (slide.offer.action === "deposit-match") return -30;
          if (slide.offer.isFeatured) return -20;
          if (slide.offer.action === "live-event") return -10;

          const rankedIndex = visibleOffers.findIndex((offer) => offer.key === slide.offer.key);
          return rankedIndex < 0 ? Number.MAX_SAFE_INTEGER : rankedIndex;
        };

        return priority(left) - priority(right);
      });

  const mobileHeroEnabled = isMobileViewport && heroSlides.length > 0;
  const desktopWin100Slide = !isMobileViewport
    ? heroSlides.find((slide) => slide.type === "win-100") || null
    : null;

  useLayoutEffect(() => {
    const heroSlot = document.getElementById("ww-mobile-promo-hero-slot");
    if (!exiting || !mobileHeroEnabled || !heroSlot || !heroSectionRef.current) {
      heroSlot?.style.removeProperty("--ww-promo-hero-exit-height");
      return undefined;
    }

    const heroHeight = Math.ceil(heroSectionRef.current.getBoundingClientRect().height || 0);
    if (heroHeight > 0) {
      heroSlot.style.setProperty("--ww-promo-hero-exit-height", `${heroHeight}px`);
    }

    return () => heroSlot.style.removeProperty("--ww-promo-hero-exit-height");
  }, [exiting, mobileHeroEnabled]);

  useLayoutEffect(() => {
    const heroSlot = document.getElementById("ww-mobile-promo-hero-slot");
    if (!entering || !mobileHeroEnabled || !heroSlot || !heroSectionRef.current) {
      setHeroEnteringActive(false);
      setHeroEnteringReady(false);
      heroSlot?.style.removeProperty("--ww-promo-hero-enter-height");
      return undefined;
    }

    const heroHeight = Math.ceil(heroSectionRef.current.getBoundingClientRect().height || 0);
    if (heroHeight <= 72) {
      setHeroEnteringActive(false);
      return undefined;
    }

    setHeroEnteringActive(true);
    setHeroEnteringReady(false);
    heroSlot.style.setProperty("--ww-promo-hero-enter-height", `${heroHeight}px`);
    const frame = window.requestAnimationFrame(() => setHeroEnteringReady(true));
    const timer = window.setTimeout(() => {
      setHeroEnteringActive(false);
      setHeroEnteringReady(false);
      heroSlot.style.removeProperty("--ww-promo-hero-enter-height");
    }, 480);

    return () => {
      window.cancelAnimationFrame(frame);
      window.clearTimeout(timer);
      heroSlot.style.removeProperty("--ww-promo-hero-enter-height");
    };
  }, [entering, mobileHeroEnabled]);

  useLayoutEffect(() => {
    const heroSlot = document.getElementById("ww-mobile-promo-hero-slot");
    heroSlot?.querySelector(".ww-promo-hero--server")?.remove();
    document.body.classList.remove("ww-promo-hero-pending");
  }, [mobileHeroEnabled]);

  useLayoutEffect(() => {
    if (typeof document === "undefined") {
      return undefined;
    }

    document.body.classList.toggle("ww-promo-hero-active", mobileHeroEnabled);

    return () => document.body.classList.remove("ww-promo-hero-active");
  }, [mobileHeroEnabled]);

  const displayedOffers = exiting
    ? []
    : tierRestricted
    ? []
    : (isMobileViewport ? (mobileHeroEnabled ? [] : visibleOffers) : visibleOffers.slice(0, 4));
  const displayedOfferSignature = displayedOffers.map((offer) => offer.key).join("|");
  const heroSlideSignature = heroSlides.map((slide) => `${slide.offer.key}-${slide.index}`).join("|");

  useEffect(() => {
    if (
      !mobileHeroEnabled ||
      heroSlides.length < 2 ||
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches
    ) {
      return undefined;
    }

    const rotationTimer = window.setTimeout(() => {
      if (document.hidden) {
        return;
      }

      const slider = heroSliderRef.current;
      const nextIndex = (activeHeroSlide + 1) % heroSlides.length;
      const nextSlide = slider?.children?.[nextIndex];
      if (slider && nextSlide) {
        slider.scrollTo({ left: nextSlide.offsetLeft, behavior: "smooth" });
        setActiveHeroSlide(nextIndex);
      }
    }, 6000);

    return () => window.clearTimeout(rotationTimer);
  }, [activeHeroSlide, heroSlides.length, mobileHeroEnabled]);

  useEffect(() => {
    displayedOffers.forEach((offer, index) => {
      if (viewedPromotionKeysRef.current.has(offer.key)) {
        return;
      }

      viewedPromotionKeysRef.current.add(offer.key);
      trackPromotionViewed(offer, { index });
    });
  }, [displayedOfferSignature]);

  useEffect(() => {
    if (!mobileHeroEnabled) {
      return;
    }

    heroSlides.forEach((slide, index) => {
      if (slide.offer.isPreview) {
        return;
      }
      const trackingKey = `hero-${slide.offer.key}-${slide.index}`;
      if (viewedPromotionKeysRef.current.has(trackingKey)) {
        return;
      }

      viewedPromotionKeysRef.current.add(trackingKey);
      const trackedTitle = slide.type === "deposit-match" ? slide.offer.title : slide.header;
      trackPromotionViewed(
        {
          ...slide.offer,
          promotionName: trackedTitle || slide.offer.promotionName,
          title: trackedTitle || slide.offer.title
        },
        { index, slot: "top_picks_mobile_hero" }
      );
    });
  }, [heroSlideSignature, mobileHeroEnabled]);

  if (displayedOffers.length === 0 && !mobileHeroEnabled && !desktopWin100Slide) {
    return null;
  }

  const dismissOffer = (event, offerKey) => {
    event.preventDefault();
    event.stopPropagation();

    const nextDismissedOfferKeys = [...new Set([...dismissedOfferKeys, offerKey])];

    try {
      window.localStorage.setItem(
        PROMO_DISMISS_STORAGE_KEY,
        JSON.stringify({
          date: getTodayDismissKey(),
          keys: nextDismissedOfferKeys
        })
      );
    } catch (e) {
      // Ignore storage failures and still dismiss in-memory.
    }

    setDismissedOfferKeys(nextDismissedOfferKeys);
  };

  const openRegisterModal = () => {
    const modalTrigger = document.getElementById("register-link");
    if (modalTrigger instanceof HTMLElement) {
      modalTrigger.click();
      return;
    }

    if (window.jQuery) {
      window.jQuery("#register-modal").modal("show");
    }
  };

  const handleWin100GetStarted = () => {
    if (loggedIn) {
	  if (document.body.classList.contains("ww-win-100-qualified")) {
		window.location.href = WIN_100_BOARD_URL;
		return;
	  }
	  if (window.jQuery) {
		window.jQuery("#win-100-offer-modal").modal("hide");
	  }
	  window.setTimeout(() => {
		if (typeof window.wwOpenAddBalance === "function") {
		  window.wwOpenAddBalance({ offer: "win100", minimumAmount: 10 });
		} else {
		  window.dispatchEvent(new CustomEvent("ww:add-balance-sheet-open", {
			detail: { offer: "win100", minimumAmount: 10 }
		  }));
		}
	  }, 250);
      return;
    }

    if (window.jQuery) {
      window.jQuery("#win-100-offer-modal").modal("hide");
      window.setTimeout(openRegisterModal, 250);
      return;
    }

    openRegisterModal();
  };

  const openPromoInfoModal = () => {
    if (typeof onOpenInfoModal === "function") {
      onOpenInfoModal("promo");
      return true;
    }

    if (window.jQuery && window.jQuery("#promo-modal").length) {
      window.jQuery("#promo-modal").modal("show");
      return true;
    }

    return false;
  };

  const openUnavailablePromoModal = (offer) => {
    const details = offer.isFeatured && !offer.isEligible
      ? (UNAVAILABLE_PROMO_MODAL_DETAILS[offer.action] || INACTIVE_PROMO_MODAL_DETAILS[offer.action])
      : INACTIVE_PROMO_MODAL_DETAILS[offer.action];

    if (!details) {
      return false;
    }

    setInactivePromoModal(details);
    return true;
  };

  const handleOfferClick = (event, offer, trackingContext = null) => {
    if (offer.isPreview) {
      event.preventDefault();
      toastr["info"]("This hero is in preview mode. Its live CTA will open the matching event.", "Hero Preview");
      return;
    }

    trackPromotionSelected(
      offer,
      trackingContext || { index: displayedOffers.findIndex((item) => item.key === offer.key) }
    );

    if (offer.action === "live-event") {
      event.preventDefault();
      if (typeof onSelectLiveEvent === "function" && offer.liveEvent) {
        onSelectLiveEvent(offer.liveEvent);
      }
      return;
    }

    if (offer.action === "win-100") {
      event.preventDefault();
      setWin100DetailsVisible(false);
      setWin100ModalOpen(true);
      return;
    }

    if (offer.action === "free-square") {
      event.preventDefault();

      const promoUsed = hasRecentDiscountedStatPromoUse();
      if (loggedIn && (!getTapInEligibility() || promoUsed)) {
        setTapInEligible(false);
        setDiscountedStatPromoUsed(promoUsed);
        setTapInUnavailable(true);
        toastr["warning"](
          "This discount is no longer available for your account. Refresh the board to see current offers.",
          "Promo Unavailable"
        );
        return;
      }

      const tapInPickButtons = document.querySelectorAll("#free-squares-modal [data-ww-tap-pick]");
      const clickedHeroCta = event.target instanceof Element && event.target.closest(".ww-promo-hero__cta");
      if (clickedHeroCta && tapInPickButtons.length === 1) {
        tapInPickButtons[0].click();
        return;
      }

      if (window.jQuery && window.jQuery("#free-squares-modal").length) {
        window.jQuery("#free-squares-modal").modal("show");
        return;
      }

      setTapInUnavailable(true);
      toastr["warning"](
        "That discounted line is no longer on the current board. Please choose another pick.",
        "Promo Updated"
      );
      return;
    }

    if (offer.action === "boost" && offer.isActivated) {
      event.preventDefault();
      clearBoostValue();
      toastr["info"]("Boost has been deactivated.", "Boost Removed");
      return;
    }

    if (offer.action === "risk-free" && offer.isActivated) {
      event.preventDefault();
      clearRiskFreeValue();
      toastr["info"]("Throwback Thursday has been deactivated.", "Throwback Removed");
      return;
    }

    if (offer.isActivated) {
      event.preventDefault();
      return;
    }

    if (!offer.isClaimableNow && !offer.isActivated && openUnavailablePromoModal(offer)) {
      event.preventDefault();
      return;
    }

    if (!loggedIn) {
      event.preventDefault();
      openRegisterModal();
      return;
    }

    if (!offer.isClaimableNow) {
      event.preventDefault();
      return;
    }

    event.preventDefault();

    celebratePromoActivation();

    if (offer.action === "deposit-match") {
      if (typeof window.wwOpenAddBalance === "function") {
        window.wwOpenAddBalance();
        return;
      }

      if (window.jQuery) {
        window.jQuery("#add-balance").modal("show");
      }
      return;
    }

    if (offer.action === "risk-free") {
      setRiskFreeValue("5");
      toastr["success"]("Throwback is active on your next eligible entry for up to $5.", "Offer Activated");
      return;
    }

    if (offer.action === "boost") {
      setBoostValue("20", 24 * 60 * 60);
      toastr["success"]("20% Boost is active and payout has been updated below.", "Boost Activated");
    }

    if (offer.action === "refer-friend") {
      window.location.href = "/invite-friends/";
    }

  };

  const showPromoInfo = (event, offer) => {
    event.preventDefault();
    event.stopPropagation();
    trackPromotionSelected(offer, {
      index: displayedOffers.findIndex((item) => item.key === offer.key),
      slot: "athlete_board_promotion_details",
    });

    const details = PROMO_INFO_DETAILS[offer.key];
    if (details) {
      setPromoInfoModal({
        ...details,
        title: offer.key === "free-square" ? offer.title : details.title
      });
    }
  };

  const handleHeroScroll = () => {
    const slider = heroSliderRef.current;
    if (!slider || heroSlides.length < 2) {
      return;
    }

    const firstSlide = slider.firstElementChild;
    const slideWidth = firstSlide?.getBoundingClientRect().width || slider.clientWidth;
    const gap = Number.parseFloat(window.getComputedStyle(slider).columnGap || "0") || 0;
    const nextIndex = Math.max(0, Math.min(heroSlides.length - 1, Math.round(slider.scrollLeft / (slideWidth + gap))));
    setActiveHeroSlide(nextIndex);
  };

  const selectHeroSlide = (index) => {
    const slider = heroSliderRef.current;
    const slide = slider?.children?.[index];
    if (!slider || !slide) {
      return;
    }

    slider.scrollTo({ left: slide.offsetLeft, behavior: "smooth" });
    setActiveHeroSlide(index);
  };

  const renderMobileHero = () => {
    const heroSlot = document.getElementById("ww-mobile-promo-hero-slot");
    if (!mobileHeroEnabled || !heroSlot) {
      return null;
    }

    return createPortal(
      <section
        className={`ww-promo-hero${heroSlides.length === 1 ? " ww-promo-hero--single" : ""}${exiting ? " ww-promo-hero--exiting" : ""}${heroEnteringActive ? " ww-promo-hero--entering" : ""}${heroEnteringReady ? " ww-promo-hero--entering-ready" : ""}`}
        ref={heroSectionRef}
        aria-hidden={exiting || undefined}
        aria-label="Featured promotions"
      >
        <div className="ww-promo-hero__slider" ref={heroSliderRef} onScroll={handleHeroScroll}>
          {heroSlides.map((slide, slideIndex) => {
            const isLiveEventSlide = Boolean(getLiveHeroFamily(slide.type));
            const isChecking = isLiveEventSlide && pendingEventKey === getEventAvailabilityKey(slide.offer.liveEvent);
            const heroHeader = slide.type === "deposit-match" ? slide.offer.title : slide.header;
            const trackingOffer = {
              ...slide.offer,
              promotionName: heroHeader || slide.offer.promotionName,
              title: heroHeader || slide.offer.title
            };
            const heroCta = slide.offer.action === "boost" && slide.offer.isActivated
              ? "Deactivate"
              : !slide.offer.isClaimableNow &&
                !slide.offer.isActivated &&
                INACTIVE_PROMO_MODAL_DETAILS[slide.offer.action]
                  ? "More info"
                  : (slide.cta || (isLiveEventSlide ? "View Live Event" : ""));
            const showPromoAthlete = slide.type === "stat-discount" && resolvedActivePromoAthlete;
            const promoAthleteStat = resolvedActivePromoAthlete?.stat ?? resolvedActivePromoAthlete?.stat_value ?? "";
            const promoAthleteOriginalStat = resolvedActivePromoAthlete?.og_value ?? "";

            return (
              <article data-ww-lcp-slide={slideIndex + 1} className={`ww-promo-hero__slide ww-promo-hero__slide--${slide.type}${showPromoAthlete ? " has-athlete-card" : ""}`} key={`${slide.offer.key}-${slide.index}`}>
                <img
                  className="ww-promo-hero__background"
                  src={slide.background_url}
                  alt=""
                  aria-hidden="true"
                  width="768"
                  height="430"
                  loading={slideIndex === 0 ? "eager" : "lazy"}
                  fetchPriority={slideIndex === 0 ? "high" : "low"}
                  decoding="async"
                />
                <a
                  className="ww-promo-hero__link"
                  aria-busy={isChecking || undefined}
                  aria-disabled={isChecking || undefined}
                  href={slide.offer.isClaimableNow ? slide.offer.link : "#"}
                  onClick={(event) => {
                    if (isChecking) {
                      event.preventDefault();
                      return;
                    }
                    handleOfferClick(
                      event,
                      trackingOffer,
                      { index: slideIndex, slot: "top_picks_mobile_hero" }
                    );
                  }}
                >
                  {isLiveEventSlide ? (
                    <span className="ww-promo-hero__live-event">
                      {heroHeader && <span className="ww-promo-hero__live-heading">{heroHeader}</span>}
                      <span className="ww-promo-hero__live-status">
                        <span className="ww-promo-hero__live-dot" aria-hidden="true" />
                        {getLiveEventStatusLabel(slide.offer.liveEvent)}
                      </span>
                      <span className="ww-promo-hero__live-teams">
                        {[
                          {
                            side: "away",
                            name: slide.offer.liveEvent.away_full || slide.offer.liveEvent.away,
                            logo: slide.offer.liveEvent.away_logo,
                            score: slide.offer.liveEvent.away_score
                          },
                          {
                            side: "home",
                            name: slide.offer.liveEvent.home_full || slide.offer.liveEvent.home,
                            logo: slide.offer.liveEvent.home_logo,
                            score: slide.offer.liveEvent.home_score
                          }
                        ].map((team) => (
                          <span className="ww-promo-hero__live-team" key={team.side}>
                            {team.logo ? (
                              <img src={team.logo} alt="" aria-hidden="true" loading="lazy" decoding="async" />
                            ) : (
                              <span className="ww-promo-hero__live-team-fallback" aria-hidden="true">
                                {String(team.name || "?").slice(0, 1)}
                              </span>
                            )}
                            <strong>{team.name || "TBD"}</strong>
                            <span className="ww-promo-hero__live-score">{team.score ?? "–"}</span>
                          </span>
                        ))}
                      </span>
                      {slide.subheader && <span className="ww-promo-hero__live-subheader">{slide.subheader}</span>}
                      {heroCta && (
                        <span className={`ww-promo-hero__cta${isChecking ? " is-checking" : ""}`}>
                          {isChecking ? (
                            <span
                              className="ww-event-check-spinner"
                              role="status"
                              aria-label="Checking event availability"
                            />
                          ) : (
                            <>{heroCta}<span aria-hidden="true">›</span></>
                          )}
                        </span>
                      )}
                    </span>
                  ) : (
                    <span className="ww-promo-hero__copy">
                      <h2 className="ww-promo-hero__title">{heroHeader}</h2>
                      {slide.subheader && <span className="ww-promo-hero__subheader">{slide.subheader}</span>}
                      {heroCta && <span className="ww-promo-hero__cta">{heroCta}<span aria-hidden="true">›</span></span>}
                    </span>
                  )}
                  {showPromoAthlete && (
                    <span className="ww-promo-hero__athlete-card">
                      <span className="ww-promo-hero__athlete-header">
                        {resolvedActivePromoAthlete.image && (
                          <img className="ww-promo-hero__athlete-image" src={resolvedActivePromoAthlete.image} alt="" aria-hidden="true" loading="lazy" decoding="async" />
                        )}
                        <span className="ww-promo-hero__athlete-name">{resolvedActivePromoAthlete.name}</span>
                      </span>
                      <span className="ww-promo-hero__athlete-stat-label">{resolvedActivePromoAthlete.stat_display}</span>
                      <span className="ww-promo-hero__athlete-values">
                        <strong>@ {promoAthleteStat}</strong>
                        {promoAthleteOriginalStat !== "" && <span>Was <s>{promoAthleteOriginalStat}</s></span>}
                      </span>
                    </span>
                  )}
                </a>
              </article>
            );
          })}
        </div>
        {heroSlides.length > 1 && (
          <div className="ww-promo-hero__indicators" aria-label="Choose promotion">
            {heroSlides.map((slide, index) => (
              <button
                type="button"
                key={`${slide.offer.key}-indicator-${slide.index}`}
                className={index === activeHeroSlide ? "is-active" : ""}
                aria-label={`Show promotion ${index + 1}`}
                aria-current={index === activeHeroSlide ? "true" : undefined}
                onClick={() => selectHeroSlide(index)}
              />
            ))}
          </div>
        )}
      </section>,
      heroSlot
    );
  };

  const renderDesktopWin100Hero = () => {
    const heroSlot = document.getElementById("ww-mobile-promo-hero-slot");
    if (!desktopWin100Slide || !heroSlot) {
      return null;
    }

    const heroHeader = desktopWin100Slide.header || "Play $5, Get $100";
    const heroCta = desktopWin100Slide.cta || "Get Started";
    const trackingOffer = {
      ...desktopWin100Slide.offer,
      promotionName: heroHeader,
      title: heroHeader
    };

    return createPortal(
      <section className="ww-promo-hero ww-promo-hero--desktop-win100" aria-label="Win $100 promotion">
        <article data-ww-lcp-slide="1" className="ww-promo-hero__slide ww-promo-hero__slide--win-100">
          <img
            className="ww-promo-hero__background"
            src={desktopWin100Slide.background_url}
            alt=""
            aria-hidden="true"
            width="1440"
            height="360"
            loading="eager"
            fetchPriority="high"
            decoding="async"
          />
          <a
            className="ww-promo-hero__link"
            href={desktopWin100Slide.offer.link}
            onClick={(event) => handleOfferClick(event, trackingOffer, { slot: "top_picks_desktop_hero" })}
          >
            <span className="ww-promo-hero__copy">
              <h2 className="ww-promo-hero__title">{heroHeader}</h2>
              {desktopWin100Slide.subheader && (
                <span className="ww-promo-hero__subheader">{desktopWin100Slide.subheader}</span>
              )}
              <span className="ww-promo-hero__cta">{heroCta}<span aria-hidden="true">›</span></span>
            </span>
          </a>
        </article>
      </section>,
      heroSlot
    );
  };

  const renderOfferCard = (offer, offerIndex) => {
    const opensInactivePromoModal =
      !offer.isClaimableNow &&
      !offer.isActivated &&
      Boolean(INACTIVE_PROMO_MODAL_DETAILS[offer.action]);
    const opensRegisterModal = !loggedIn && offer.action !== "free-square" && !opensInactivePromoModal;
    const isActiveTapInPromo = offer.key === "free-square" && freeSquareActivePromoCount > 0;
    const isPrimaryImage = offerIndex === 0;

    return (
      <div
        key={offer.key}
        className={`promo-offer-card${offer.isFeatured ? " is-featured" : ""}${offer.backgroundImage ? " has-background-image" : ""}${offer.key === "deposit-match" ? " promo-offer-card--deposit-match" : ""}`}
        onClick={(event) => handleOfferClick(event, offer)}
        style={{
          ...(offer.cardStyle || {})
        }}
      >
        {offer.key !== "money-back" && !isActiveTapInPromo && (
          <button
            type="button"
            className="promo-offer-card__dismiss"
            aria-label={`Dismiss ${offer.title}`}
            onClick={(event) => dismissOffer(event, offer.key)}
          >
            <XMarkIcon aria-hidden="true" className="ww-inline-icon" />
          </button>
        )}

        {offer.backgroundImage && (
          <span
            className="promo-offer-card__background-image"
            aria-hidden="true"
          >
            <img
              className="promo-offer-card__image"
              src={offer.backgroundImage}
              alt=""
              aria-hidden="true"
              width={offer.backgroundImageWidth}
              height={offer.backgroundImageHeight}
              loading={isPrimaryImage ? "eager" : "lazy"}
              fetchPriority={isPrimaryImage ? "high" : "low"}
              decoding="async"
              draggable={false}
            />
          </span>
        )}

        <a
          href={offer.isClaimableNow ? offer.link : "#"}
          className="promo-offer-card__link"
          data-toggle={opensRegisterModal ? "modal" : undefined}
          data-target={opensRegisterModal ? "#register-modal" : undefined}
        >
          <span className="promo-offer-card__eyebrow">{offer.status}</span>
          <h3
            className="promo-offer-card__title"
            aria-label={offer.key === "deposit-match" ? offer.title : undefined}
          >
            {offer.key === "deposit-match" ? (
              <>
                <span className="promo-offer-card__title-line promo-offer-card__title-line--first" aria-hidden="true">
                  <span className="promo-offer-card__title-word promo-offer-card__title-word--match" data-text="Match">Match</span>
                  <span className="promo-offer-card__title-word promo-offer-card__title-word--your" data-text="Your">Your</span>
                </span>
                <span className="promo-offer-card__title-line promo-offer-card__title-line--second" aria-hidden="true">
                  <span
                    className="promo-offer-card__title-word promo-offer-card__title-word--first"
                    data-text={loggedIn ? "Next" : "First"}
                  >
                    {loggedIn ? "Next" : "First"}
                  </span>
                  <span className="promo-offer-card__title-word promo-offer-card__title-word--deposit" data-text="Deposit">Deposit</span>
                </span>
              </>
            ) : offer.title}
          </h3>
          {offer.description && <p className="promo-offer-card__description">{offer.description}</p>}
          <span className="promo-offer-card__footer">
            {offer.key !== "free-square" && (offer.isClaimableNow || offer.isActivated) && (
              <span className={`promo-offer-card__cta${offer.isActivated ? " is-active" : ""}`}>
                {offer.isActivated
                  ? (["boost", "risk-free"].includes(offer.action) ? "Deactivate" : "Activated")
                  : offer.key === "deposit-match" ? "Claim Bonus" : offer.isFeatured ? "Activate" : "Get Offer"}
              </span>
            )}
          </span>
        </a>
        {PROMO_INFO_DETAILS[offer.key] && (
          <button
            type="button"
            className="promo-offer-card__info"
            aria-label={`More information about ${offer.title}`}
            onClick={(event) => showPromoInfo(event, offer)}
          >
            Offer Details
          </button>
        )}
      </div>
    );
  };

  return (
    <>
      {renderMobileHero()}
      {renderDesktopWin100Hero()}
      {displayedOffers.length > 0 && (
        <div className="promo-offers-shell mt-2">
          <div className={`promo-offers${isMobileViewport ? " promo-offers--mobile-slider" : ""}`}>
            {displayedOffers.map((offer, offerIndex) => renderOfferCard(offer, offerIndex))}
          </div>
        </div>
      )}
      {inactivePromoModal && (
        <div className="modal fade" id="promo-offer-unavailable-modal" tabIndex={-1} role="dialog" aria-labelledby="promo-offer-unavailable-modal-label" aria-hidden="true">
          <div className="modal-dialog" role="document">
            <div className="modal-content">
              <div className="modal-header text-center">
                <div id="promo-offer-unavailable-modal-label" className="w-100">{inactivePromoModal.title}</div>
                <button type="button" className="close" data-dismiss="modal" aria-label="Close">
                  <span aria-hidden="true">&times;</span>
                </button>
              </div>
              <div className="modal-body text-center">
                <p>{inactivePromoModal.body}</p>
                <button type="button" className="btn btn-primary w-100 mt-3" data-dismiss="modal">Got It</button>
              </div>
            </div>
          </div>
        </div>
      )}
      {win100ModalOpen && (
        <div className="modal fade win-100-offer-modal" id="win-100-offer-modal" tabIndex={-1} role="dialog" aria-labelledby="win-100-offer-modal-label" aria-hidden="true">
          <div className="modal-dialog modal-dialog-centered" role="document">
            <div className="modal-content">
              <button type="button" className="close win-100-offer-modal__close" data-dismiss="modal" aria-label="Close">
                <span aria-hidden="true">&times;</span>
              </button>
              <div className="modal-body">
                <span className="win-100-offer-modal__eyebrow">Limited-time bonus</span>
                <h2 id="win-100-offer-modal-label">PLAY $5, WIN $100</h2>
                <p className="win-100-offer-modal__intro">Your first $5 Challenge is on us.</p>
                <ol className="win-100-offer-modal__steps">
                  <li><strong>Deposit $10+, Get Free Entry</strong></li>
                  <li><strong>Pick 4 from 4 different matchups</strong></li>
                  <li><strong>Hit 4 → $100 <span aria-hidden="true">•</span> Hit 3 → $10</strong></li>
                </ol>
                <p className="win-100-offer-modal__funds">Winnings awarded as promo funds.</p>
                <button type="button" className="btn btn-primary win-100-offer-modal__start" onClick={handleWin100GetStarted}>GET STARTED</button>
                <button
                  type="button"
                  className="win-100-offer-modal__details-toggle"
                  aria-expanded={win100DetailsVisible}
                  onClick={() => setWin100DetailsVisible((visible) => !visible)}
                >
                  Offer details &amp; rules
                </button>
                {win100DetailsVisible && (
                  <div className="win-100-offer-modal__details">
                    <p>Requires a minimum $10 deposit. The challenge entry uses exactly four unique athletes from four different events.</p>
                    <p>Eligible major-league offerings only. Promotional discounts, selections below 55% odds, and selections without odds are excluded.</p>
                    <p>Two or more DNP, draw, or cancelled selections unlock another chance at the offer. Promo funds and eligibility remain subject to the promotional terms.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      {promoInfoModal && (
        <div className="modal fade promo-offer-info-modal" id="promo-offer-info-modal" tabIndex={-1} role="dialog" aria-labelledby="promo-offer-info-modal-label" aria-hidden="true">
          <div className="modal-dialog modal-dialog-centered" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <div>
                  <span className="promo-offer-info-modal__eyebrow">Offer details</span>
                  <h2 id="promo-offer-info-modal-label" className="promo-offer-info-modal__title">{promoInfoModal.title}</h2>
                </div>
                <button type="button" className="close" data-dismiss="modal" aria-label="Close">
                  <span aria-hidden="true">&times;</span>
                </button>
              </div>
              <div className="modal-body">
                <p className="promo-offer-info-modal__intro">{promoInfoModal.intro}</p>
                <h3>How it works</h3>
                <ol>
                  {promoInfoModal.steps.map((step) => <li key={step}>{step}</li>)}
                </ol>
                <h3>Rules</h3>
                <ul>
                  {promoInfoModal.rules.map((rule) => <li key={rule}>{rule}</li>)}
                </ul>
                <p className="promo-offer-info-modal__note">
                  {promoInfoModal.note || "Offer availability is determined when you submit your entry."}
                </p>
                <button type="button" className="btn btn-primary w-100 mt-2" data-dismiss="modal">Got It</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PromoOffers;
import { XMarkIcon } from "../../icons/heroIcons";
